import { CSSProperties, FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@tremor/react';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import ScheduleIcon from '@mui/icons-material/Schedule';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import OutboxOutlinedIcon from '@mui/icons-material/OutboxOutlined';
import { RequestStatus } from '../../services/service-evaluations';
import { EvaluationRecipientStatus } from '../../util/evaluationContract';
import { recipientStatusMeta } from '../../util/evaluationStatusMeta';
import styles from './page-supplier-evaluations.module.scss';

type Tiles = RequestStatus['tiles'];

type Props = {
  tiles: Tiles;
};

type Tile = {
  key: keyof Tiles;
  title: string;
  subtitle?: string;
  tone: string;
  icon: ReactNode;
  onlyWhenAny?: boolean;
};

const toneStyle = (status: string): CSSProperties => {
  const meta = recipientStatusMeta(status);
  return { '--tone-bg': meta.backgroundColor, '--tone-text': meta.color } as CSSProperties;
};

const ICON_SIZE = { fontSize: 16 };

const StatusTiles: FC<Props> = ({ tiles }) => {
  const [t] = useTranslation('evaluations');

  const list: Tile[] = [
    {
      key: 'recipients',
      title: t('status.tiles.recipients', 'Mottagare'),
      tone: EvaluationRecipientStatus.NotSent,
      icon: <PeopleAltOutlinedIcon style={ICON_SIZE} />,
    },
    {
      key: 'answered',
      title: t('status.tiles.answered', 'Besvarade'),
      tone: EvaluationRecipientStatus.Answered,
      icon: <CheckCircleOutlinedIcon style={ICON_SIZE} />,
    },
    {
      key: 'declined',
      title: t('status.tiles.declined', 'Kan inte bedöma'),
      tone: EvaluationRecipientStatus.Declined,
      icon: <BlockOutlinedIcon style={ICON_SIZE} />,
    },
    {
      key: 'waiting',
      title: t('status.tiles.waiting', 'Väntar'),
      subtitle:
        tiles.overdue > 0
          ? t('status.tiles.waitingOverdue', {
              count: tiles.overdue,
              defaultValue_one: 'varav {{count}} försenad',
              defaultValue_other: 'varav {{count}} försenade',
            })
          : undefined,
      tone: EvaluationRecipientStatus.Sent,
      icon: <ScheduleIcon style={ICON_SIZE} />,
    },
    {
      key: 'failed',
      title: t('status.tiles.failed', 'Misslyckade'),
      tone: EvaluationRecipientStatus.Failed,
      onlyWhenAny: true,
      icon: <ErrorOutlineIcon style={ICON_SIZE} />,
    },
    {
      key: 'notSent',
      title: t('status.tiles.notSent', 'Ej skickade'),
      tone: EvaluationRecipientStatus.NotSent,
      icon: <OutboxOutlinedIcon style={ICON_SIZE} />,
      onlyWhenAny: true,
    },
  ];

  return (
    <div className={styles.metricsGrid}>
      {list
        .filter(tile => !tile.onlyWhenAny || tiles[tile.key] > 0)
        .map(tile => {
          const tone = toneStyle(
            tiles[tile.key] > 0 ? tile.tone : EvaluationRecipientStatus.NotSent,
          );
          return (
            <Card key={tile.key} className={styles.metricCard}>
              <div className={styles.metricCardText} data-tile={tile.key}>
                <p className={styles.metricCardTitle}>{tile.title}</p>
                <p className={styles.metricCardNumber} style={tone} data-tile-count="">
                  {tiles[tile.key]}
                </p>
                {tile.subtitle && (
                  <p className={`${styles.metricCardSubtitle} ${styles.metricCardSubtitleAlert}`}>
                    {tile.subtitle}
                  </p>
                )}
              </div>
              <div className={styles.metricCardIcon} style={tone}>
                {tile.icon}
              </div>
            </Card>
          );
        })}
    </div>
  );
};

export default StatusTiles;
